import { useCallback } from "react";
import { useQueryClient, type InfiniteData } from "@tanstack/react-query";
import {
  getChatControllerGetMessagesQueryKey,
  type ChatControllerGetMessages200,
  type ChatMessageResponseDto,
} from "@semo/api-contracts";
import type { PendingChatMessage, PendingQuoteMessage } from "../types/message";

/**
 * Union type for all possible message shapes in cache
 */
export type CacheMessage =
  | PendingChatMessage
  | PendingQuoteMessage
  | ChatMessageResponseDto;

/**
 * Hook for managing React Query cache for chat messages
 * Acts as a Client-side Repository/Store
 */
/** 배열이 아닌 일반 객체인가 — 메세지 칸 단위 병합에 쓴다. */
function isPlainRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

export function useChatCache(roomId: string) {
  const queryClient = useQueryClient();
  const queryKey = [
    ...getChatControllerGetMessagesQueryKey(roomId),
    "infinite",
  ];

  /**
   * Add or Update a message in the infinite query cache
   * @param newMessage The message data (real or optimistic)
   * @param targetTempId Optional tempId to find and replace (for optimistic update success)
   * @param options.cancelInFlightFetch Realtime 수신처럼 **서버 응답이 모르는 사실**을 넣을 때 true
   */
  const upsertMessage = useCallback(
    (
      newMessage: CacheMessage,
      targetTempId?: string,
      options?: { cancelInFlightFetch?: boolean },
    ) => {
      // 진행 중인 GET /messages 는 이 메시지가 생기기 전의 목록을 싣고 온다. 그 응답이
      // 뒤늦게 도착하면 pages 를 통째로 갈아끼우며 방금 넣은 메시지를 지운다 —
      // 계약 직후 [원하시는 계약서류] 카드가 깜빡이고 사라지던 원인이 이것이다
      // (계약서류 메시지는 계약 응답 뒤에 비동기로 만들어지는데, 프론트는 계약 성공
      //  즉시 메시지 목록을 invalidate 한다). 그래서 캐시에 닿기 전에 끊는다.
      //
      // 두 가지를 지켜야 한다:
      //  1) 반드시 setQueryData **보다 먼저**. cancelQueries 는 revert:true 가 기본이라
      //     fetch 시작 시점 스냅샷으로 state 를 되돌리는데, 이 되돌리기가 cancel() 안에서
      //     동기로 끝난다. 순서가 반대면 방금 넣은 메시지가 revert 에 지워진다.
      //  2) 캐시가 비어 있으면(최초 로딩 중) 취소하지 않는다. 초기 로딩을 끊으면 방금 받은
      //     한 건만 남고 나머지 메시지를 통째로 잃는다.
      //
      // 취소된 요청은 에러가 아니라 현재 데이터로 resolve 되므로 수신 로직이 깨지지 않는다.
      // 그 요청이 실어오던 다른 갱신분은 버려지지만, Realtime 이 살아 있는 순간에만
      // 취소하므로 후속 이벤트/재조회로 따라잡는다.
      if (
        options?.cancelInFlightFetch &&
        queryClient.getQueryData(queryKey) !== undefined
      ) {
        void queryClient.cancelQueries({ queryKey, exact: true });
      }

      queryClient.setQueryData<InfiniteData<ChatControllerGetMessages200>>(
        queryKey,
        (oldData) => {
          // console.log(`[useChatCache] oldData for room ${roomId}:`, !!oldData);
          if (!oldData?.pages || oldData.pages.length === 0) {
            // Initialize cache if empty
            const emptyPage = {
              success: true,
              timestamp: new Date().toISOString(),
              data: {
                items: [newMessage],
                meta: {
                  hasNext: false,
                  totalCount: 1,
                  itemCount: 1,
                  pageSize: 20,
                  totalPages: 1,
                  currentPage: 1,
                  hasPrev: false,
                },
              },
            } as unknown as ChatControllerGetMessages200;

            return {
              pages: [emptyPage],
              pageParams: [undefined],
            };
          }

          const newPages = [...oldData.pages];
          // Deep clone the first page (newest messages) to avoid mutation
          const latestPage = { ...newPages[0] };
          const latestData = {
            ...(latestPage.data || { items: [], meta: {} }),
          };
          const items = [...(latestData.items || [])] as CacheMessage[];

          // Check for duplicates by ID (Real ID match)
          const existingIndex = items.findIndex((m) => m.id === newMessage.id);

          // Check for duplicates by Temp ID (Optimistic -> Real transition)
          let tempIndex = -1;

          if (targetTempId) {
            // Precise replacement using provided tempId
            tempIndex = items.findIndex(
              (m) => "tempId" in m && m.tempId === targetTempId,
            );
          } else if ((newMessage as any).tempId) {
            // ✅ Match by tempId from server response/realtime event (Fix for race conditions)
            const target = (newMessage as any).tempId;
            tempIndex = items.findIndex((m) => (m as any).tempId === target);
          } else {
            // Fallback heuristic (Legacy support, generally avoid relying on this)
            tempIndex = items.findIndex(
              (m) =>
                "tempId" in m &&
                m.senderId === newMessage.senderId &&
                m.messageType === newMessage.messageType &&
                ("tempId" in newMessage
                  ? m.tempId === newMessage.tempId
                  : true),
            );
          }

          // ✅ Normalize Message Data (Adapter Pattern)
          // Ensure 'paymentPendingData' exists if 'metadata' is present for payment_pending messages.
          if (
            newMessage.messageType === "payment_pending" &&
            "metadata" in newMessage
          ) {
            // Cast to allow assignment since ChatMessageResponseDto might not have metadata in type definition if strict
            const msg = newMessage as any;
            msg.paymentPendingData = {
              ...(msg.paymentPendingData || {}),
              ...msg.metadata,
            };
          }

          if (existingIndex !== -1) {
            // Update existing real message
            const existingMsg = items[existingIndex];

            // Guard against undefined access (TS18048)
            if (!existingMsg) return oldData;

            // If the existing message is real (not pending) and the incoming message is pending,
            // ignore the incoming message to prevent degrading the rich data from the server.
            if (
              !(existingMsg as any).isPending &&
              (newMessage as any).isPending
            ) {
              return oldData;
            }

            let mergedMessage = { ...existingMsg, ...newMessage };

            // Deep merge for quoteData to support partial updates (e.g. async PDF URL)
            if (
              "quoteData" in newMessage &&
              newMessage.quoteData &&
              "quoteData" in existingMsg &&
              existingMsg.quoteData
            ) {
              const existingQuoteData = existingMsg.quoteData;
              mergedMessage = {
                ...mergedMessage,
                quoteData: {
                  ...existingQuoteData,
                  ...newMessage.quoteData,
                },
              };
            }

            /**
             * contractInfo 도 칸 단위로 합친다(GS A3-03).
             *
             * 계약서 PDF 가 다 만들어지면 서버가 «메세지 갱신» 신호에 `contractInfo: { pdfUrl }`
             * 한 칸만 실어 보낸다. 얕게 덮어쓰면 status·contractId 가 사라져 ContractMessage 가
             * 아무 카드도 그리지 않았다 — 발주기관 채팅의 «거래명세서» 카드가 몇 초 뒤 빈 칸이 됐다.
             */
            const incomingContractInfo: unknown =
              "contractInfo" in newMessage ? newMessage.contractInfo : undefined;
            const existingContractInfo: unknown =
              "contractInfo" in existingMsg
                ? existingMsg.contractInfo
                : undefined;
            if (
              isPlainRecord(incomingContractInfo) &&
              isPlainRecord(existingContractInfo)
            ) {
              mergedMessage = Object.assign({}, mergedMessage, {
                contractInfo: {
                  ...existingContractInfo,
                  ...incomingContractInfo,
                },
              });
            }

            // Ensure we merge the normalized paymentPendingData with existing data
            if (
              "paymentPendingData" in newMessage &&
              newMessage.paymentPendingData
            ) {
              // We need to cast because not all CacheMessage types have paymentPendingData
              const existingPaymentMsg = existingMsg as any;
              mergedMessage = {
                ...mergedMessage,
                paymentPendingData: {
                  ...(existingPaymentMsg.paymentPendingData || {}),
                  ...newMessage.paymentPendingData,
                },
              };
            }

            // If updating a real message that was previously pending (edge case), clear pending flags
            // Though usually this path is for updates to already-real messages.
            items[existingIndex] = mergedMessage;

            // If we found a temp message that corresponds to this real message (duplicate case), remove it
            if (tempIndex !== -1 && tempIndex !== existingIndex) {
              items.splice(tempIndex, 1);
            }
          } else if (tempIndex !== -1) {
            // Replace temp message with real message (Optimistic Success)
            // Merge allows preserving local UI state if needed, but usually we want to replace with server state.
            // Important: `newMessage` from server won't have `isPending` or `tempId`.
            // We overwrite the old object entirely but keep any local state if we want?
            // No, for success, we want the server state. Local flags will be cleared since they aren't in newMessage.
            items[tempIndex] = {
              ...items[tempIndex],
              ...newMessage,
              isPending: false,
              isError: false,
              tempId: undefined,
            } as CacheMessage;
          } else {
            // Add new message to the top
            items.unshift(newMessage);
            // Resort by createdAt desc (lexicographical sort for ISO strings is faster than Date parsing)
            items.sort((a, b) => {
              if (b.createdAt > a.createdAt) return 1;
              if (b.createdAt < a.createdAt) return -1;
              return 0;
            });
          }

          // Re-assign items to latestData
          // Need type assertion because items is CacheMessage[] but ChatControllerGetMessages200 expects specific structure
          (latestData as any).items = items;
          latestPage.data = latestData as any;
          newPages[0] = latestPage as ChatControllerGetMessages200;

          return {
            ...oldData,
            pages: newPages,
          };
        },
      );
    },
    [queryClient, queryKey],
  );

  /**
   * Remove a message from cache (e.g. rollback on error)
   */
  const removeMessage = useCallback(
    (messageId: string, tempId?: string) => {
      queryClient.setQueryData<InfiniteData<ChatControllerGetMessages200>>(
        queryKey,
        (oldData) => {
          if (!oldData) return oldData;

          const newPages = oldData.pages.map((page) => {
            if (!page.data?.items) return page;

            const items = page.data.items as CacheMessage[];
            const newItems = items.filter((m) => {
              if (tempId && "tempId" in m && m.tempId === tempId) return false;
              if (m.id === messageId) return false;
              return true;
            });

            return {
              ...page,
              data: { ...page.data, items: newItems },
            } as unknown as ChatControllerGetMessages200;
          });

          return { ...oldData, pages: newPages };
        },
      );
    },
    [queryClient, queryKey],
  );

  /**
   * Mark messages as read optimistically
   * Updates all messages sent by current user up to the lastReadMessageId
   */
  const markAsReadOptimistic = useCallback(
    (lastReadMessageId: string, currentUserId: string) => {
      queryClient.setQueryData<InfiniteData<ChatControllerGetMessages200>>(
        queryKey,
        (oldData) => {
          if (!oldData) return oldData;

          // 1. Find the target message to establish the cutoff time
          let cutoffDate: Date | null = null;

          for (const page of oldData.pages) {
            if (!page.data?.items) continue;
            const targetMsg = page.data.items.find(
              (m) => m.id === lastReadMessageId,
            );
            if (targetMsg) {
              cutoffDate = new Date(targetMsg.createdAt);
              break;
            }
          }

          // If target message not found in cache, we can't determine cutoff.
          // Ideally we should refetch, but for optimistic update we might just abort
          // or assume it's very recent. Safety first: abort.
          if (!cutoffDate) return oldData;

          const newPages = oldData.pages.map((page) => {
            if (!page.data?.items) return page;

            const items = page.data.items as CacheMessage[];
            const newItems = items.map((msg) => {
              const isMyMessage = msg.senderId === currentUserId;

              // Logic: If it's my message AND it's created before or at the same time as the read receipt
              if (isMyMessage && !msg.isRead) {
                const msgDate = new Date(msg.createdAt);
                if (msgDate <= cutoffDate) {
                  return { ...msg, isRead: true };
                }
              }
              return msg;
            });

            return {
              ...page,
              data: { ...page.data, items: newItems },
            } as unknown as ChatControllerGetMessages200;
          });

          return { ...oldData, pages: newPages };
        },
      );
    },
    [queryClient, queryKey],
  );

  return {
    upsertMessage,
    removeMessage,
    markAsReadOptimistic,
    invalidate: () => queryClient.invalidateQueries({ queryKey }),
  };
}
